// src/pages/Businesses.js - Business Management Page
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline'

export default function Businesses() {
  const [businesses, setBusinesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedBusiness, setSelectedBusiness] = useState(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    fetchBusinesses()
  }, [])

  const fetchBusinesses = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('business_info')
        .select('*')
        .order('business_name', { ascending: true })

      if (error) throw error
      setBusinesses(data || [])
    } catch (error) {
      console.error('Error fetching businesses:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredBusinesses = businesses.filter(business =>
    business.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    business.business_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    business.owner_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getStatusColor = (active) => {
    return active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A'
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
    }
    return phone
  }

  const BusinessModal = () => {
    if (!selectedBusiness) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Business Details</h3>
            <button
              onClick={() => setSelectedBusiness(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Basic Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-600">Business Name:</label>
                      <p className="font-medium">{selectedBusiness.business_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Type:</label>
                      <p className="font-medium capitalize">{selectedBusiness.business_type}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Owner:</label>
                      <p className="font-medium">{selectedBusiness.owner_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Status:</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedBusiness.active)}`}>
                        {selectedBusiness.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Contact Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-600">Phone Number:</label>
                      <p className="font-medium">{formatPhoneNumber(selectedBusiness.phone_number)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Owner Phone:</label>
                      <p className="font-medium">{formatPhoneNumber(selectedBusiness.owner_phone)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Staff Phone:</label>
                      <p className="font-medium">{formatPhoneNumber(selectedBusiness.staff_phone)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Information */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Service Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-600">Service Area:</label>
                      <p className="font-medium">{selectedBusiness.service_area || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Hours:</label>
                      <p className="font-medium">{selectedBusiness.hours || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Pricing Info:</label>
                      <p className="font-medium">{selectedBusiness.pricing_info || 'Contact for pricing'}</p>
                    </div>
                  </div>
                </div>

                {/* Services */}
                {selectedBusiness.services && selectedBusiness.services.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Services</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedBusiness.services.map((service, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Emergency Keywords */}
                {selectedBusiness.emergency_keywords && selectedBusiness.emergency_keywords.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Emergency Keywords</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedBusiness.emergency_keywords.map((keyword, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Agent Information */}
            {selectedBusiness.elevenlabs_agent_id && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Agent Configuration</h4>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm font-mono text-gray-700">{selectedBusiness.elevenlabs_agent_id}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Business Management</h1>
            <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Business
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Search */}
        <div className="mb-6">
          <div className="max-w-md">
            <input
              type="text"
              placeholder="Search businesses..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Businesses Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBusinesses.map((business) => (
              <div key={business.id} className="bg-white rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {business.business_name}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(business.active)}`}>
                      {business.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="capitalize">{business.business_type}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{formatPhoneNumber(business.phone_number)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{business.service_area || 'Service area not set'}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{business.owner_name}</span>
                    </div>
                  </div>

                  {/* Services */}
                  {business.services && business.services.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {business.services.slice(0, 3).map((service, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {service}
                          </span>
                        ))}
                        {business.services.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            +{business.services.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => setSelectedBusiness(business)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View Details
                    </button>
                    <div className="flex space-x-2">
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button className="p-1 text-red-400 hover:text-red-600">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredBusinesses.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">No businesses found matching your search.</p>
          </div>
        )}
      </div>

      <BusinessModal />
    </div>
  )
}